<template>
  <div class="k-editable-table-container" ref="mainScroll">
    <table class="table table-hoverable k-edit-table">
      <thead>
        <tr>
          <th v-for="(entry, index) in headers" :key="index" class="k-edit-table-th">
            <div class="k-edit-table-header">
              <span class="header-left">
                <span class="header-title" v-html="$kpurify.sanitise(entry.title)"></span>
                <div class="header-controls">
                  <div class="table-search" v-if="entry.filterable">
                    <input :id="`editable-table-search-${$.uid}-${entry.key || index}`" type="text" class="form-control search-input ph-no-capture" v-model="searchFilters[entry.key]">
                    <button type="submit" title="Submit" aria-label="Submit">
                      <label :for="`editable-table-search-${$.uid}-${entry.key || index}`"><i class="fa fa-search" aria-hidden="true"></i></label>
                    </button>
                  </div>
                  <div class="header-control-icon">
                    <button type="button" class="btn btn-filters header-control-button" v-if="entry.sortable !== false" @click="setSort(entry)" title="Sort" :aria-label="`Sort by ${entry.title}`">
                      <i :class="sortClass" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <kr v-if="paginatedRows.length === 0"
          :headers="headers"
          :loading="loading"
          :emptyMessage="emptyMessage"
          :class-func="rowClassFunc"
        ></kr>
        <kr
          v-else
          v-for="row in paginatedRows"
          :key="row._index"
          :row="row"
          :class-func="rowClassFunc"
          :headers="headers"
          :timezone="timezone"
          :preserved-editing-state="editingStates[row._index] || {}"
          @update="updateCell(row._index, $event)"
          @commitEdits="commitEdits"
          @update-editing-state="updateEditingState"
        ></kr>
      </tbody>
    </table>
    <pagination class="k-editable-table-pagination" v-model="currentPage" :count="numOfPages" v-if="numOfPages" :total-entries="modelValue.length" :displayed-entries="paginatedRows.length"></pagination>
  </div>
</template>

<style>
table.k-edit-table,
.k-edit-table > tbody > tr > th,
.k-edit-table > tbody > tr > td {
  border-collapse: collapse;
  border: 1px solid var(--kate-background);
}

.k-edit-table-th {
  background-color: var(--kate-background-body);
  color: var(--kate-type-light);
}

.header-controls {
  line-height: 1;
  display: flex;
}

.k-edit-table-header {
  display: block;
  min-width: max-content;
  line-height: 2;
  white-space: nowrap;
}

.k-edit-table .btn-filters {
  background-color: transparent;
  color: var(--kate-type-link);
  border: 0;
}

.k-edit-table .btn-filters:hover {
  background-color: var(--kate-primary-dark);
  color: var(--kate-type-light);
}

.k-edit-table .btn-filters:active {
  background-color: var(--kate-primary-dark);
  color: var(--kate-type-light);
}

.k-edit-table .btn-filters:focus {
  background-color: var(--kate-primary-dark);
  color: var(--kate-type-light);
}

.k-edit-table .btn-filters i.fas.fa-trash {
  color: var(--kate-danger-alt);
}

.k-edit-table-header .header-left {
  display: flex;
  align-items: center;
}

.k-editable-table-pagination.panel-pagination.row {
  margin: 15px -15px;
}

.k-editable-table-row:nth-child(even) {
  background-color: var(--kate-background-body-alpha);
}

.k-editable-table-row:nth-child(odd) {
  background-color: var(--kate-background-alt-alpha);
}

</style>

<script>
/* eslint no-underscore-dangle: ["error", { "allow": ["_index"] }] */
import stringMatch from '@utils/string-match';
import TableDropdownOverflowMixin from '@mixins/table-dropdown-overflow-mixin';
import KRow from './k-editable-table-row.vue';
import Pagination from './k-pagination.vue';

export default {
  components: {
    kr: KRow,
    Pagination,
  },

  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    rowClassFunc: {
      type: Function,
    },
    initialSortState: {
      type: Object,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: 'No data found',
    },
    max: Number,
    timezone: String,
  },

  mixins: [TableDropdownOverflowMixin],

  watch: {
    numOfPages() {
      this.currentPage = 0;
    },
    initialSortState: {
      immediate: true,
      handler(newSortState) {
        if (newSortState) {
          this.currentSortHeader = this.headers.find(h => h.key === newSortState.headerKey);
          this.sortAscending = newSortState.ascending;
        }
      },
    },
  },

  data: () => ({
    popover: {
      visibility: 'focus',
    },
    searchFilters: {},
    currentSortHeader: null,
    sortAscending: true,
    currentPage: 0,
    editingStates: {},
  }),

  computed: {
    refinedRows() {
      return this.modelValue
        .map((x, index) => ({ ...x, _index: index }))
        .filter(this.isRowVisible)
        .sort(this.sortingFunction);
    },
    sortMultiplier() {
      return this.sortAscending ? 1 : -1;
    },
    sortClass() {
      return this.sortAscending ? 'fas fa-sort-amount-up' : 'fas fa-sort-amount-down';
    },

    /* Pagination */
    numOfPages() {
      if (!this.max || this.refinedRows.length <= this.max) {
        return undefined;
      }
      return Math.ceil(this.refinedRows.length / this.max);
    },

    paginatedRows() {
      if (!this.numOfPages) {
        return this.refinedRows;
      }
      const output = [];
      const startingIndex = this.max * this.currentPage;
      const endingIndex = Math.min(this.max + startingIndex, this.refinedRows.length);
      for (let i = startingIndex; i < endingIndex; i++) {
        output.push(this.refinedRows[i]);
      }
      return output;
    },
  },

  methods: {
    updateEditingState(rowIndex, headerKey, editingState) {
      if (!this.editingStates[rowIndex]) {
        this.editingStates[rowIndex] = {};
      }
      if (editingState) {
        this.editingStates[rowIndex][headerKey] = editingState;
      } else {
        delete this.editingStates[rowIndex][headerKey];
      }
    },
    isRowVisible(row) {
      let header;
      let key;
      let valToAnalyse;
      for (let i = 0; i < this.headers.length; i++) {
        header = this.headers[i];
        key = header.key;
        if (header.filterable && key in this.searchFilters) {
          if ('text' in header) {
            valToAnalyse = header.text(row[key]);
          } else {
            valToAnalyse = row[key];
          }
          const filterText = this.searchFilters[key];
          if (Array.isArray(valToAnalyse)) {
            return Boolean(valToAnalyse.find(x => stringMatch(x, filterText, true))
                                                  || (valToAnalyse.length === 0 && filterText === ''));
          }
          if (!stringMatch(valToAnalyse, filterText)) {
            return false;
          }
        }
      }
      return true;
    },
    update(newRows) {
      this.$emit('update:modelValue', newRows);
    },
    updateCell(index, nuval) {
      const newRows = this.modelValue.slice();
      newRows[index] = nuval;
      this.update(newRows);
    },
    commitEdits(payload) {
      this.$emit('commitEdits', payload);
    },
    setSort(header) {
      if (this.currentSortHeader && this.currentSortHeader.key === header.key) {
        this.sortAscending = !this.sortAscending;
      } else {
        this.currentSortHeader = header;
        this.sortAscending = true;
      }
      this.$emit('sort-changed', {
        headerKey: this.currentSortHeader.key,
        ascending: this.sortAscending,
      });
    },
    sortingFunction(a, b) {
      if (!this.currentSortHeader) {
        return 0;
      }
      // New rows pending save changes are always sorted to top
      if (a.is_pending && b.is_pending) {
        return a._index - b._index;
      }
      if (a.is_pending) return -1;
      if (b.is_pending) return 1;

      // Only sort saved rows
      const aObj = a[this.currentSortHeader.key];
      const bObj = b[this.currentSortHeader.key];
      let A;
      let B;
      if ('sortBy' in this.currentSortHeader && aObj && bObj) {
        A = this.currentSortHeader.sortBy(aObj);
        B = this.currentSortHeader.sortBy(bObj);
      } else {
        A = aObj;
        B = bObj;
      }
      if (this.currentSortHeader.type === 'boolean') {
        // Convert null to false so unchecked boxes are sorted normally, not nulls to bottom
        if (A == null) A = false;
        if (B == null) B = false;
      }
      if (A == null && B == null) {
        return 0;
      }
      if (A == null) {
        return 1;
      }
      if (B == null) {
        return -1;
      }

      // KSB sorting passes an array from the sortBy function
      if (Array.isArray(A) && Array.isArray(B) && A.length === 2 && B.length === 2) {
        // Compare first element (category) - K=1, S=2, B=3
        if (A[0] < B[0]) return -1 * this.sortMultiplier;
        if (A[0] > B[0]) return 1 * this.sortMultiplier;

        // If first elements are equal, compare second element (number)
        const a1 = Number(A[1]);
        const b1 = Number(B[1]);
        if (a1 < b1) return -1 * this.sortMultiplier;
        if (a1 > b1) return 1 * this.sortMultiplier;

        return 0;
      }

      if (A < B) {
        return -1 * this.sortMultiplier;
      }
      if (A > B) {
        return 1 * this.sortMultiplier;
      }
      return 0;
    },
  },
};
</script>
